import { useState, useEffect } from "react";
import API from "../../services/api";

interface Coupon {
  _id: string;
  name: string;
  coupon_code: string;
  start_date: string;
  end_date: string;
  per_user_count: number;
  all_count: number;
  status: 'active' | 'inactive' | 'expired';
}

interface EditCouponModalProps {
  coupon: Coupon;
  onClose: () => void;
  onSuccess: () => void;
}

interface ValidationError {
  type: string;
  msg: string;
  path: string;
  location: string;
}

export default function EditCouponModal({ coupon, onClose, onSuccess }: EditCouponModalProps) {
  const [formData, setFormData] = useState({
    name: "",
    coupon_code: "",
    start_date: "",
    end_date: "",
    per_user_count: 1,
    all_count: 100,
    status: "active"
  });
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [generalError, setGeneralError] = useState<string | null>(null);

  useEffect(() => {
    const formatDate = (date: string) => {
      const d = new Date(date);
      return d.toISOString().slice(0, 16);
    };

    setFormData({
      name: coupon.name,
      coupon_code: coupon.coupon_code,
      start_date: formatDate(coupon.start_date),
      end_date: formatDate(coupon.end_date),
      per_user_count: coupon.per_user_count,
      all_count: coupon.all_count,
      status: coupon.status
    });
  }, [coupon]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setFieldErrors({});
    setGeneralError(null);

    try {
      const res = await API.put(`/coupon/${coupon._id}`, formData);
      if (res.data && res.data.success) {
        onSuccess();
      }
    } catch (error: any) {
      if (error?.response?.data?.data && Array.isArray(error.response.data.data)) {
        const errors: { [key: string]: string } = {};
        error.response.data.data.forEach((err: ValidationError) => {
          errors[err.path] = err.msg;
        });
        setFieldErrors(errors);
        // setGeneralError(error?.response?.data?.message || "Validation Error");
      } else {
        setGeneralError(error?.response?.data?.message || "Failed to update coupon");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = e.target.type === 'number' ? parseInt(e.target.value) || 0 : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
    if (fieldErrors[e.target.name]) {
      setFieldErrors({
        ...fieldErrors,
        [e.target.name]: ""
      });
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Edit Coupon</h2>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Coupon Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Summer Sale 2024"
              style={{
                ...styles.input,
                ...(fieldErrors.name ? styles.inputError : {})
              }}
            />
            {fieldErrors.name && (
              <span style={styles.fieldError}>{fieldErrors.name}</span>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Coupon Code *</label>
            <input
              type="text"
              name="coupon_code"
              value={formData.coupon_code}
              onChange={handleChange}
              placeholder="e.g., SUMMER2024"
              style={{
                ...styles.input,
                ...(fieldErrors.coupon_code ? styles.inputError : {})
              }}
            />
            {fieldErrors.coupon_code && (
              <span style={styles.fieldError}>{fieldErrors.coupon_code}</span>
            )}
            <small style={styles.helperText}>Letters and numbers only, no spaces</small>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Start Date *</label>
              <input
                type="datetime-local"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  ...(fieldErrors.start_date ? styles.inputError : {})
                }}
              />
              {fieldErrors.start_date && (
                <span style={styles.fieldError}>{fieldErrors.start_date}</span>
              )}
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>End Date *</label>
              <input
                type="datetime-local"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  ...(fieldErrors.end_date ? styles.inputError : {})
                }}
              />
              {fieldErrors.end_date && (
                <span style={styles.fieldError}>{fieldErrors.end_date}</span>
              )}
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Per User Limit *</label>
              <input
                type="number"
                name="per_user_count"
                value={formData.per_user_count}
                onChange={handleChange}
                min="1"
                max="100"
                style={{
                  ...styles.input,
                  ...(fieldErrors.per_user_count ? styles.inputError : {})
                }}
              />
              {fieldErrors.per_user_count && (
                <span style={styles.fieldError}>{fieldErrors.per_user_count}</span>
              )}
              <small style={styles.helperText}>Max uses per user (1-100)</small>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Total Limit *</label>
              <input
                type="number"
                name="all_count"
                value={formData.all_count}
                onChange={handleChange}
                min="1"
                max="10000"
                style={{
                  ...styles.input,
                  ...(fieldErrors.all_count ? styles.inputError : {})
                }}
              />
              {fieldErrors.all_count && (
                <span style={styles.fieldError}>{fieldErrors.all_count}</span>
              )}
              <small style={styles.helperText}>Total uses across all users (1-10000)</small>
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              style={styles.select}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          {generalError && (
            <div style={styles.errorMessage}>
              {generalError}
            </div>
          )}

          <div style={styles.modalFooter}>
            <button type="button" onClick={onClose} style={styles.cancelButton}>
              Cancel
            </button>
            <button type="submit" disabled={loading} style={styles.submitButton}>
              {loading ? "Updating..." : "Update Coupon"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "32px",
    maxWidth: "600px",
    width: "90%",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
  modalTitle: {
    fontSize: "22px",
    fontWeight: "600",
    color: "#1a2332",
    margin: 0,
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    color: "#666",
    padding: "0 4px",
  },
  formGroup: {
    marginBottom: "16px",
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
  },
  label: {
    display: "block",
    fontSize: "14px",
    fontWeight: "500",
    color: "#555",
    marginBottom: "6px",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "14px",
    outline: "none",
    transition: "border-color 0.2s",
    boxSizing: "border-box",
  },
  inputError: {
    borderColor: "#f44336",
    backgroundColor: "#fff3f3",
  },
  select: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "14px",
    outline: "none",
    backgroundColor: "#fff",
  },
  helperText: {
    display: "block",
    marginTop: "4px",
    fontSize: "12px",
    color: "#888",
  },
  fieldError: {
    display: "block",
    marginTop: "4px",
    fontSize: "12px",
    color: "#f44336",
  },
  errorMessage: {
    padding: "10px 14px",
    backgroundColor: "#fff3f3",
    border: "1px solid #f44336",
    borderRadius: "6px",
    color: "#d32f2f",
    fontSize: "14px",
    marginBottom: "16px",
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "24px",
    paddingTop: "16px",
    borderTop: "1px solid #e8ecf1",
  },
  cancelButton: {
    padding: "10px 20px",
    backgroundColor: "#f5f5f5",
    color: "#666",
    border: "1px solid #ddd",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  submitButton: {
    padding: "10px 24px",
    backgroundColor: "#6c5ce7",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "background-color 0.2s",
  },
};